import { useCallback, useEffect, useMemo, useRef } from "react";
import { reconcileMainMapRuntime } from "./mainMapRuntime.js";
import { useLiveMapFeed } from "./useLiveMapFeed.js";
import { useLiveVehicleMapData } from "./useLiveVehicleMapData.js";
import { useMapStyleLifecycle } from "./useMapStyleLifecycle.js";
const emptyFeatureCollection = Object.freeze({
  type: "FeatureCollection",
  features: Object.freeze([])
});
const returnEmptyFeatureCollection = () => emptyFeatureCollection;
export function useMainMapRuntime({
  mapRef,
  style,
  runtimeState,
  liveData = {},
  onError
}) {
  const {
    showVehicles = false,
    showScooters = false,
    buildVehicleFeatureCollection = returnEmptyFeatureCollection,
    buildScooterFeatureCollection = returnEmptyFeatureCollection,
    vehicleRefreshInterval = 15000,
    scooterRefreshInterval = 30000,
    scooterEndpoint = "/api/micromobility/mapdata",
    staleAfterMs = 90000
  } = liveData;
  const vehicleData = useLiveVehicleMapData({
    enabled: showVehicles,
    refreshInterval: vehicleRefreshInterval,
    staleAfterMs
  });
  const vehiclePositions = useMemo(() => vehicleData.hasVehicleMapData ? buildVehicleFeatureCollection(vehicleData.vehicleRecords) : emptyFeatureCollection, [buildVehicleFeatureCollection, vehicleData.hasVehicleMapData, vehicleData.vehicleRecords]);
  const scooterData = useLiveMapFeed({
    enabled: showScooters,
    endpoint: scooterEndpoint,
    refreshInterval: scooterRefreshInterval,
    staleAfterMs,
    buildFeatureCollection: buildScooterFeatureCollection
  });
  const resolvedRuntimeState = {
    ...runtimeState,
    vehiclePositions,
    showVehicles,
    scooterPositions: scooterData.featureCollection,
    showScooters,
    ensureVehicleBadges: runtimeState.ensureVehicleBadges ? () => runtimeState.ensureVehicleBadges(vehiclePositions) : undefined,
    ensureScooterImages: runtimeState.ensureScooterImages ? () => runtimeState.ensureScooterImages(scooterData.featureCollection) : undefined
  };
  const stateRef = useRef(resolvedRuntimeState);
  const lifecycleRef = useRef(null);
  stateRef.current = resolvedRuntimeState;
  const reconcile = useCallback(async ({
    revision,
    isCurrent
  }) => {
    await reconcileMainMapRuntime({
      ...stateRef.current,
      map: mapRef.current,
      revision,
      isCurrent,
      requestReconcile: () => lifecycleRef.current?.requestReconcile(),
      onError
    });
  }, [mapRef, onError]);
  const lifecycle = useMapStyleLifecycle({
    mapRef,
    style,
    onError,
    reconcile
  });
  lifecycleRef.current = lifecycle;
  const reconcileState = useMemo(() => ({
    isDark: runtimeState.isDark,
    preferSatellite: runtimeState.preferSatellite,
    favourites: runtimeState.favourites,
    selection: runtimeState.selection,
    stopLayerIds: runtimeState.stopLayerIds,
    stopSourceConfig: runtimeState.stopSourceConfig,
    selectedTripShape: runtimeState.selectedTripShape,
    vehiclePositions,
    showVehicles,
    scooterPositions: scooterData.featureCollection,
    showScooters
  }), [runtimeState.isDark, runtimeState.preferSatellite, runtimeState.favourites, runtimeState.selection, runtimeState.stopLayerIds?.stops, runtimeState.stopLayerIds?.activeStops, runtimeState.stopSourceConfig, runtimeState.selectedTripShape, vehiclePositions, showVehicles, scooterData.featureCollection, showScooters]);
  useEffect(() => {
    if (lifecycle.readyRevision) void lifecycle.requestReconcile();
  }, [lifecycle.readyRevision, lifecycle.requestReconcile, reconcileState]);
  return {
    ...lifecycle,
    vehiclePositions,
    vehiclePositionsError: vehicleData.error,
    vehiclePositionsIsLoading: vehicleData.isLoading,
    refreshVehiclePositions: vehicleData.refresh,
    scooterPositions: scooterData.featureCollection,
    scooterPositionsError: scooterData.error,
    scooterPositionsIsLoading: scooterData.isLoading
  };
}